import { z } from 'zod';
import { UpdateCommonFilterPayload } from './commonFilter.type';
import { Filter_Config, Filter_State } from './filters.type';
import { KeyWordFilter_Config, KeyWordFilter_State } from './keywordFilter.type';
import {
    NumberFilter_Config_S,
    NumberFilter_ControlsState_S,
    NumberFilter_RequestParams_S,
    NumberFilter_Snapshot_S,
    NumberFilter_State_S,
    NumberFilterAggregation_S,
    NumberFilterValue_S,
    NumberFilterValues_Config_S,
    NumberFilterValues_ControlsState_S,
} from './numberFilter.schema';

export type NumberFilterValue = z.infer<typeof NumberFilterValue_S>;

/**
 * NUMBER FILTER CONFIGURATION
 * */
export type NumberFilterValues_Config = z.infer<typeof NumberFilterValues_Config_S>;

export type NumberFilter_Config = z.infer<typeof NumberFilter_Config_S>;

export const isNumberRangeFilterConfig = (filter: Filter_Config): filter is NumberFilter_Config => {
    return (
        (filter.type === 'integer' || filter.type === 'float') &&
        'values' in filter &&
        'min' in filter.values &&
        'max' in filter.values &&
        'value' in filter.values.min &&
        'value' in filter.values.max
    );
};

// if filter.configuration.explicitValues === true and filter.values.keywords is defined, then the number filter acts as a keyword filter
export const isNumberRangeFilterAsKeywordsConfig = (filter: Filter_Config): filter is KeyWordFilter_Config => {
    return (
        (filter.type === 'integer' || filter.type === 'float') &&
        filter.configuration.subtype === 'explicitValues' &&
        'values' in filter &&
        'keywords' in filter.values &&
        Array.isArray(filter.values.keywords) &&
        filter.values.keywords.length > 0 &&
        'key' in filter.values.keywords[0] &&
        'doc_count' in filter.values.keywords[0]
    );
};

/**
 * NUMBER FILTER STATE
 * */
export type NumberFilter_State = z.infer<typeof NumberFilter_State_S>;

export const isNumberFilterState = (filter: Filter_State): filter is NumberFilter_State => {
    return (
        (filter.type === 'integer' || filter.type === 'float') &&
        'values' in filter &&
        'min' in filter.values &&
        'max' in filter.values &&
        'value' in filter.values.min &&
        'value' in filter.values.max &&
        'currentValue' in filter.values.min &&
        'currentValue' in filter.values.max
    );
};

export const isNumberRangeFilterAsKeywordsState = (filter: Filter_State): filter is KeyWordFilter_State => {
    return (
        (filter.type === 'integer' || filter.type === 'float') &&
        filter.configuration.subtype === 'explicitValues' &&
        'values' in filter &&
        'keywords' in filter.values &&
        Array.isArray(filter.values.keywords) &&
        filter.values.keywords.length > 0 &&
        'key' in filter.values.keywords[0] &&
        'doc_count' in filter.values.keywords[0] &&
        'isChecked' in filter.values.keywords[0]
    );
};

/**
 * NUMBER FILTER CONTROLS STATE
 */
export type NumberFilterValues_ControlsState = z.infer<typeof NumberFilterValues_ControlsState_S>;

export type NumberFilter_ControlsState = z.infer<typeof NumberFilter_ControlsState_S>;

// updating controls state
export type UpdateNumberFilterValue_ControlsState = {
    min: number;
    max: number;
};

export type UpdateNumberFilterPayload_ControlsState = UpdateCommonFilterPayload & {
    value: UpdateNumberFilterValue_ControlsState;
};

/**
 * NUMBER FILTER REQUEST PARAMS
 */
export type NumberFilter_RequestParams = z.infer<typeof NumberFilter_RequestParams_S>;

/**
 * NUMBER FILTER SNAPSHOT
 * */
export type NumberFilter_Snapshot = z.infer<typeof NumberFilter_Snapshot_S>;

/**
 * NUMBER FILTER STATE AGGREGATION RESPONSE
 * */
export type NumberFilterAggregation = z.infer<typeof NumberFilterAggregation_S>;
